import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';

const ConfiguracionInvoices = () => {
  const { user, API } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadConfig();
    }
  }, [user]);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/invoice-config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/invoice-config`, config);
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Estás seguro de restablecer la configuración a valores por defecto?')) {
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/invoice-config/reset`);
      toast.success('Configuración restablecida');
      await loadConfig();
    } catch (error) {
      console.error('Error resetting config:', error);
      toast.error('Error al restablecer configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (path, value) => {
    setConfig(prevConfig => {
      const newConfig = { ...prevConfig };
      const keys = path.split('.');
      let current = newConfig;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const updateContacto = (index, field, value) => {
    setConfig(prevConfig => {
      const newConfig = { ...prevConfig };
      newConfig.contactos[index][field] = value;
      return newConfig;
    });
  };

  const addContacto = () => {
    setConfig(prevConfig => ({
      ...prevConfig,
      contactos: [
        ...prevConfig.contactos,
        {
          nombre: '',
          cargo: '',
          telefono: '',
          es_whatsapp: true,
          orden: prevConfig.contactos.length + 1
        }
      ]
    }));
  };

  const removeContacto = (index) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      contactos: prevConfig.contactos.filter((_, i) => i !== index)
    }));
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="shadow-lg border-red-200">
          <CardContent className="text-center py-12">
            <i className="fas fa-lock text-red-500 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-red-800 mb-2">Acceso Restringido</h3>
            <p className="text-red-600">Solo administradores pueden configurar facturas.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
          <p className="text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-gray-600">No se pudo cargar la configuración</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              <i className="fas fa-cog mr-3 text-blue-600"></i>
              Configuración de Facturas
            </h1>
            <p className="text-gray-600">
              Configura los datos que aparecerán en las facturas enviadas a los estudiantes
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              onClick={handleReset}
              variant="outline"
              disabled={saving}
              className="text-gray-600 hover:text-gray-700"
            >
              <i className="fas fa-undo mr-2"></i>
              Restablecer
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="instituto-gradient text-white"
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Guardar Cambios
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Datos del Instituto */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardTitle className="flex items-center text-blue-800">
            <i className="fas fa-building mr-2"></i>
            Datos del Instituto
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="instituto-nombre">Nombre del Instituto</Label>
              <Input
                id="instituto-nombre"
                value={config.instituto?.nombre || ''}
                onChange={(e) => updateConfig('instituto.nombre', e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="instituto-email">Email de Soporte</Label>
              <Input
                id="instituto-email"
                type="email"
                value={config.instituto?.email_soporte || ''}
                onChange={(e) => updateConfig('instituto.email_soporte', e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="instituto-direccion">Dirección</Label>
              <Input
                id="instituto-direccion"
                value={config.instituto?.direccion || ''}
                onChange={(e) => updateConfig('instituto.direccion', e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="instituto-cp">Código Postal</Label>
              <Input
                id="instituto-cp"
                value={config.instituto?.codigo_postal || ''}
                onChange={(e) => updateConfig('instituto.codigo_postal', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Métodos de Pago */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
          <CardTitle className="flex items-center text-green-800">
            <i className="fas fa-credit-card mr-2"></i>
            Métodos de Pago
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {/* Abitab */}
          <div className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Abitab</h3>
              <Switch
                checked={config.metodos_pago?.abitab?.activo ?? true}
                onCheckedChange={(checked) => updateConfig('metodos_pago.abitab.activo', checked)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="abitab-banco">Banco</Label>
                <Input
                  id="abitab-banco"
                  value={config.metodos_pago?.abitab?.banco || ''}
                  onChange={(e) => updateConfig('metodos_pago.abitab.banco', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="abitab-tipo">Tipo de Cuenta</Label>
                <Input
                  id="abitab-tipo"
                  value={config.metodos_pago?.abitab?.tipo_cuenta || ''}
                  onChange={(e) => updateConfig('metodos_pago.abitab.tipo_cuenta', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="abitab-cuenta">Número de Cuenta</Label>
                <Input
                  id="abitab-cuenta"
                  value={config.metodos_pago?.abitab?.numero_cuenta || ''}
                  onChange={(e) => updateConfig('metodos_pago.abitab.numero_cuenta', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Transferencia */}
          <div className="mb-6 p-4 border border-green-200 rounded-lg bg-green-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Transferencia Bancaria</h3>
              <Switch
                checked={config.metodos_pago?.transferencia?.activo ?? true}
                onCheckedChange={(checked) => updateConfig('metodos_pago.transferencia.activo', checked)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="trans-cuenta">Número de Cuenta</Label>
                <Input
                  id="trans-cuenta"
                  value={config.metodos_pago?.transferencia?.numero_cuenta || ''}
                  onChange={(e) => updateConfig('metodos_pago.transferencia.numero_cuenta', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="trans-moneda">Moneda</Label>
                <Input
                  id="trans-moneda"
                  value={config.metodos_pago?.transferencia?.moneda || ''}
                  onChange={(e) => updateConfig('metodos_pago.transferencia.moneda', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="trans-titular">Titular</Label>
                <Input
                  id="trans-titular"
                  value={config.metodos_pago?.transferencia?.titular || ''}
                  onChange={(e) => updateConfig('metodos_pago.transferencia.titular', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Western Union */}
          <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Western Union (Extranjeros)</h3>
              <Switch
                checked={config.metodos_pago?.western_union?.activo ?? true}
                onCheckedChange={(checked) => updateConfig('metodos_pago.western_union.activo', checked)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="wu-beneficiario">Beneficiario</Label>
                <Input
                  id="wu-beneficiario"
                  value={config.metodos_pago?.western_union?.beneficiario || ''}
                  onChange={(e) => updateConfig('metodos_pago.western_union.beneficiario', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="wu-cedula">Cédula</Label>
                <Input
                  id="wu-cedula"
                  value={config.metodos_pago?.western_union?.cedula || ''}
                  onChange={(e) => updateConfig('metodos_pago.western_union.cedula', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="md:col-span-1">
                <Label htmlFor="wu-direccion">Dirección</Label>
                <Input
                  id="wu-direccion"
                  value={config.metodos_pago?.western_union?.direccion || ''}
                  onChange={(e) => updateConfig('metodos_pago.western_union.direccion', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contactos Post-Pago */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
          <CardTitle className="flex items-center justify-between text-purple-800">
            <div className="flex items-center">
              <i className="fas fa-phone mr-2"></i>
              Contactos Post-Pago
            </div>
            <Button onClick={addContacto} size="sm" className="bg-purple-600 hover:bg-purple-700">
              <i className="fas fa-plus mr-1"></i>
              Agregar Contacto
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {config.contactos?.map((contacto, index) => (
              <div key={index} className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold">Contacto {index + 1}</h4>
                  {config.contactos.length > 1 && (
                    <Button
                      onClick={() => removeContacto(index)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Nombre</Label>
                    <Input
                      value={contacto.nombre}
                      onChange={(e) => updateContacto(index, 'nombre', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Cargo</Label>
                    <Input
                      value={contacto.cargo}
                      onChange={(e) => updateContacto(index, 'cargo', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      value={contacto.telefono}
                      onChange={(e) => updateContacto(index, 'telefono', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={contacto.es_whatsapp}
                        onCheckedChange={(checked) => updateContacto(index, 'es_whatsapp', checked)}
                      />
                      <Label>WhatsApp</Label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Configuración de Vencimiento */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50">
          <CardTitle className="flex items-center text-orange-800">
            <i className="fas fa-calendar-alt mr-2"></i>
            Configuración de Vencimiento
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="max-w-md">
            <Label htmlFor="dias-vencimiento">Días hasta vencimiento</Label>
            <Input
              id="dias-vencimiento"
              type="number"
              min="1"
              max="365"
              value={config.dias_vencimiento || 15}
              onChange={(e) => updateConfig('dias_vencimiento', parseInt(e.target.value) || 15)}
              className="mt-1"
            />
            <p className="text-sm text-gray-500 mt-2">
              Los invoices vencerán {config.dias_vencimiento} días después de la fecha de emisión
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Mensajes */}
      <Card className="mb-6">
        <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50">
          <CardTitle className="flex items-center text-teal-800">
            <i className="fas fa-comment-alt mr-2"></i>
            Mensajes y Notas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="mensaje-bienvenida">Mensaje de Bienvenida</Label>
              <Input
                id="mensaje-bienvenida"
                value={config.mensajes?.bienvenida || ''}
                onChange={(e) => updateConfig('mensajes.bienvenida', e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="nota-importante">Nota Importante (aparece en invoice)</Label>
              <Textarea
                id="nota-importante"
                value={config.mensajes?.nota_importante || ''}
                onChange={(e) => updateConfig('mensajes.nota_importante', e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="instrucciones-pago">Instrucciones de Pago</Label>
              <Textarea
                id="instrucciones-pago"
                value={config.mensajes?.instrucciones_pago || ''}
                onChange={(e) => updateConfig('mensajes.instrucciones_pago', e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Buttons */}
      <div className="flex justify-end space-x-3">
        <Button
          onClick={handleReset}
          variant="outline"
          disabled={saving}
        >
          <i className="fas fa-undo mr-2"></i>
          Restablecer
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="instituto-gradient text-white"
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Guardando...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2"></i>
              Guardar Cambios
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ConfiguracionInvoices;
